import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../App';
import api from '../../services/api';

const ADMIN_ROLES = ['admin', 'super_admin', 'support_admin', 'team_admin'];

export default function ScratchCardPopup() {
  const [show, setShow] = useState(false);
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();

  useEffect(() => {
    // Only show for regular users — never for any admin role
    if (!user) return;
    if (ADMIN_ROLES.includes(user.role)) return;

    // Use localStorage with user ID so different users get popup independently
    const key = `scratch_popup_${user.id}`;
    if (localStorage.getItem(key)) return; // already shown to this user

    // Small delay so dashboard loads first
    const timer = setTimeout(() => {
      api.get('/scratch/status')
        .then(r => {
          if (r.data.can_claim) {
            setShow(true);
            localStorage.setItem(key, '1'); // mark shown
          }
        })
        .catch(() => {});
    }, 1500);

    return () => clearTimeout(timer);
  }, [user]);

  function handleClaim() {
    setShow(false);
    navigate('/scratch-card');
  }

  function handleDismiss() {
    setShow(false);
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
          style={{
            position:'fixed', inset:0, zIndex:9999,
            background:'rgba(0,0,0,0.75)', backdropFilter:'blur(6px)',
            display:'flex', alignItems:'center', justifyContent:'center', padding:20
          }}
          onClick={handleDismiss}>

          <motion.div
            initial={{ scale:0.7, opacity:0, y:40 }}
            animate={{ scale:1, opacity:1, y:0 }}
            exit={{ scale:0.8, opacity:0, y:20 }}
            transition={{ type:'spring', stiffness:280, damping:22 }}
            onClick={e => e.stopPropagation()}
            style={{
              width:'100%', maxWidth:340,
              background: isDark ? 'linear-gradient(145deg,#13131f,#1a1a2e)' : '#ffffff',
              border:'1px solid rgba(13,148,136,0.35)',
              borderRadius:28, overflow:'hidden',
              boxShadow:'0 32px 80px rgba(0,0,0,0.7), 0 0 60px rgba(20,184,166,0.15)'
            }}>

            <div style={{
              height:4,
              background:'linear-gradient(90deg,#f59e0b,#ef4444,#0d9488,#14b8a6)',
            }} />

            <div style={{ padding:'28px 24px 24px' }}>
              <motion.div
                animate={{ y:[-4,4,-4] }} transition={{ duration:2.5, repeat:Infinity, ease:'easeInOut' }}
                style={{ textAlign:'center', marginBottom:16 }}>
                <span style={{ fontSize:64, display:'block', lineHeight:1 }}>🎁</span>
              </motion.div>

              <h2 style={{
                textAlign:'center', fontFamily:'Outfit,sans-serif',
                fontWeight:900, fontSize:22, marginBottom:8,
                background:'linear-gradient(135deg,#fbbf24,#f59e0b,#ef4444)',
                WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', lineHeight:1.2
              }}>
                Claim Your<br/>Welcome Bonus!
              </h2>

              <p style={{
                textAlign:'center', color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)',
                fontFamily:'Outfit,sans-serif', fontSize:13, marginBottom:20, lineHeight:1.6
              }}>
                You have an unclaimed scratch card!<br/>
                Scratch it to win a surprise bonus 🤑
              </p>

              <div style={{
                background: isDark ? 'linear-gradient(135deg,rgba(20,184,166,0.12),rgba(13,148,136,0.08))' : 'rgba(20,184,166,0.06)',
                border:'1px dashed rgba(13,148,136,0.4)',
                borderRadius:16, padding:'12px 16px', marginBottom:20,
                display:'flex', alignItems:'center', gap:12
              }}>
                <div style={{ fontSize:28 }}>🃏</div>
                <div>
                  <p style={{ color: isDark ? '#a5b4fc' : '#0f172a', fontFamily:'Outfit,sans-serif', fontWeight:700, fontSize:13, margin:0 }}>
                    1 Scratch Card Available
                  </p>
                  <p style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)', fontFamily:'Outfit,sans-serif', fontSize:11, margin:0 }}>
                    Win up to ₹500 • One time only
                  </p>
                </div>
              </div>

              <motion.button
                whileTap={{ scale:0.96 }} whileHover={{ scale:1.02 }}
                onClick={handleClaim}
                style={{
                  width:'100%', padding:'14px',
                  background:'linear-gradient(135deg,#f59e0b,#ef4444)',
                  border:'none', borderRadius:16, cursor:'pointer',
                  color:'#fff', fontFamily:'Outfit,sans-serif',
                  fontWeight:800, fontSize:15,
                  boxShadow:'0 8px 24px rgba(245,158,11,0.4)',
                  marginBottom:10
                }}>
                🎰 Scratch Now!
              </motion.button>

              <button onClick={handleDismiss}
                style={{
                  width:'100%', padding:'10px', background:'transparent',
                  border:'1px solid rgba(255,255,255,0.08)',
                  borderRadius:14, cursor:'pointer',
                  color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)', fontFamily:'Outfit,sans-serif', fontSize:12
                }}>
                Maybe later
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
} 